import React, { useEffect, useRef } from 'react';
import { X } from 'lucide-react';
import { Button } from './Button';

export interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  children: React.ReactNode;
  size?: 'sm' | 'md' | 'lg';
  footer?: React.ReactNode;
}

const modalSizes = {
  sm: 'max-w-md',  // 400px
  md: 'max-w-2xl', // 600px
  lg: 'max-w-4xl',  // 800px
};

export const Modal: React.FC<ModalProps> = ({
  isOpen,
  onClose,
  title,
  children,
  size = 'md',
  footer,
}) => {
  const modalRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    const handleClickOutside = (event: MouseEvent) => {
      if (modalRef.current && !modalRef.current.contains(event.target as Node)) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      document.addEventListener('mousedown', handleClickOutside);
      document.body.style.overflow = 'hidden';
      
      // Handle iOS overscroll behavior
      document.body.style.position = 'fixed';
      document.body.style.width = '100%';
      document.body.style.top = `-${window.scrollY}px`;
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.removeEventListener('mousedown', handleClickOutside);
      
      if (isOpen) {
        const scrollY = document.body.style.top;
        document.body.style.position = '';
        document.body.style.width = '';
        document.body.style.top = '';
        document.body.style.overflow = '';
        window.scrollTo(0, parseInt(scrollY || '0') * -1);
      }
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm animate-fade-in overscroll-none"
      role="dialog"
      aria-modal="true"
      aria-labelledby="modal-title"
    >
      <div
        ref={modalRef}
        className={`
          relative bg-white rounded-lg shadow-xl w-full
          ${modalSizes[size]}
          animate-slide-up
          max-h-[calc(100vh-2rem)]
          flex flex-col
          overflow-hidden
        `}
        style={{ touchAction: 'none' }}
      >
        {/* Header */}
        <div className="flex justify-between items-center p-4 sm:p-6 border-b border-[#EAECF0] shrink-0">
          <h2 
            id="modal-title" 
            className="text-xl font-semibold text-[#101828] pr-8"
          >
            {title}
          </h2>
          <Button
            variant="secondary"
            size="sm"
            onClick={onClose}
            className="!p-2 hover:bg-[#F9FAFB] rounded-full absolute right-4 top-4"
            aria-label="Close modal"
          >
            <X className="w-5 h-5 text-[#667085]" />
          </Button>
        </div>

        {/* Content */}
        <div 
          ref={contentRef}
          className="p-4 sm:p-6 overflow-y-auto overscroll-contain"
          style={{
            WebkitOverflowScrolling: 'touch',
            scrollbarWidth: 'thin',
            scrollbarColor: '#D0D5DD transparent'
          }}
        >
          {children}
        </div>

        {/* Footer */}
        {footer && (
          <div className="flex justify-end gap-3 p-4 sm:p-6 border-t border-[#EAECF0] bg-[#F9FAFB] shrink-0">
            {footer}
          </div>
        )}
      </div>
    </div>
  );
};